import { Icon } from '../Icon';

/**
 * In-session governance overlay (session-governance §6d).
 * A bottom-anchored toast/banner — not a blocking modal — for three governance moments:
 *   - still-there: idle check-in
 *   - time-warning: session nearing its time limit
 *   - quality-guard: ambient noise / recognition quality suggestions
 *
 * Renders nothing when event is null.
 */

export type GovernanceEvent =
  | { type: 'still-there'; onAcknowledge: () => void; onHangUp: () => void }
  | {
      type: 'time-warning';
      secondsLeft: number;
      onExtend?: () => void;
      onHangUp: () => void;
    }
  | {
      type: 'quality-guard';
      suggestion: 'push-to-talk' | 'type' | 'mute-background';
      onAcknowledge: () => void;
    };

type QualitySuggestion = 'push-to-talk' | 'type' | 'mute-background';

type Props = {
  event: GovernanceEvent | null;
};

const QUALITY_MESSAGES: Record<QualitySuggestion, string> = {
  'push-to-talk': 'Hall noise detected — try push-to-talk for clearer recognition',
  type: 'Hard to hear? Type your message instead',
  'mute-background': 'Background noise detected — pause the session or mute mic',
};

function qualityIcon(suggestion: QualitySuggestion): 'mic' | 'keyboard' {
  return suggestion === 'type' ? 'keyboard' : 'mic';
}

export function SessionGovernanceOverlay({ event }: Props) {
  if (!event) return null;

  return (
    <output
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[var(--z-overlay)] max-w-md w-full px-4"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="reveal surface-card p-4 shadow-soft">
        {event.type === 'still-there' && (
          <StillThere onAcknowledge={event.onAcknowledge} onHangUp={event.onHangUp} />
        )}
        {event.type === 'time-warning' && (
          <TimeWarning
            secondsLeft={event.secondsLeft}
            onExtend={event.onExtend}
            onHangUp={event.onHangUp}
          />
        )}
        {event.type === 'quality-guard' && (
          <QualityGuard suggestion={event.suggestion} onAcknowledge={event.onAcknowledge} />
        )}
      </div>
    </output>
  );
}

function StillThere({
  onAcknowledge,
  onHangUp,
}: {
  onAcknowledge: () => void;
  onHangUp: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Icon name="clock" size={16} className="text-subtle shrink-0" />
        <p className="font-display text-base text-ink">Are you still there?</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onAcknowledge}
          className="btn-primary px-4 py-2.5 text-sm min-h-[44px]"
        >
          Yes, continue
        </button>
        <button
          type="button"
          onClick={onHangUp}
          className="btn-ghost px-3 py-2 text-sm min-h-[44px]"
        >
          End session
        </button>
      </div>
    </div>
  );
}

function TimeWarning({
  secondsLeft,
  onExtend,
  onHangUp,
}: {
  secondsLeft: number;
  onExtend?: () => void;
  onHangUp: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Icon name="timer" size={16} className="text-subtle shrink-0" />
        <p className="font-display text-base text-ink">Coming up on time</p>
      </div>
      <p className="text-subtle text-sm">
        About {secondsLeft}s remaining. Shall I send your spec sheet?
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onHangUp}
          className="btn-primary px-4 py-2.5 text-sm min-h-[44px]"
        >
          End &amp; send spec sheet
        </button>
        {onExtend ? (
          <button
            type="button"
            onClick={onExtend}
            className="btn-ghost px-3 py-2 text-sm min-h-[44px]"
          >
            Need more time?
          </button>
        ) : null}
      </div>
    </div>
  );
}

function QualityGuard({
  suggestion,
  onAcknowledge,
}: {
  suggestion: QualitySuggestion;
  onAcknowledge: () => void;
}) {
  const message = QUALITY_MESSAGES[suggestion];
  return (
    <div className="flex items-start gap-3">
      <Icon name={qualityIcon(suggestion)} size={16} className="text-subtle shrink-0 mt-0.5" />
      <p className="flex-1 text-sm text-ink">{message}</p>
      <button
        type="button"
        onClick={onAcknowledge}
        aria-label="Dismiss"
        className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-subtle hover:bg-fill hover:text-ink transition-colors duration-1 ease-physical"
      >
        <Icon name="x" size={15} />
      </button>
    </div>
  );
}
